/** Matches orchestrator CookieForwardingService — JWT only. */
export const JWT_COOKIE_NAME = 'JWT';

export function readAllowedCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  if (typeof document === 'undefined' || !document.cookie) {
    return cookies;
  }

  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) continue;
    const name = trimmed.slice(0, eq).trim();
    const value = trimmed.slice(eq + 1);
    if (name === JWT_COOKIE_NAME) {
      cookies[name] = value;
    }
  }

  return cookies;
}

export function buildCookieHeader(cookies: Record<string, string>): string {
  const jwt = cookies[JWT_COOKIE_NAME];
  return jwt ? `${JWT_COOKIE_NAME}=${jwt}` : '';
}

/** Generic cookie read, used for cross-subdomain conversation continuity. */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) continue;
    if (trimmed.slice(0, eq).trim() === name) {
      try {
        return decodeURIComponent(trimmed.slice(eq + 1));
      } catch {
        return trimmed.slice(eq + 1);
      }
    }
  }
  return undefined;
}

export function writeCookie(
  name: string,
  value: string,
  options: { domain?: string; days?: number } = {},
): void {
  if (typeof document === 'undefined') return;
  const days = options.days ?? 365;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/', `Expires=${expires}`, 'SameSite=Lax'];
  if (options.domain) {
    parts.push(`Domain=${options.domain}`);
  }
  if (typeof location !== 'undefined' && location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
}

const CONVERSATION_COOKIE_NAME = 'orca_chat_cid';

/** Reads the shared conversation id cookie (visible on this host regardless of who set its Domain). */
export function readConversationCookie(): string | undefined {
  return readCookie(CONVERSATION_COOKIE_NAME);
}

/** Writes the conversation id cookie, scoped to `domain` (e.g. '.turbify.com') so other apps on the same site can resume it. */
export function writeConversationCookie(conversationId: string, domain?: string): void {
  writeCookie(CONVERSATION_COOKIE_NAME, conversationId, { domain });
}

/** Expires the shared conversation id cookie, e.g. when the user starts a brand new conversation. */
export function clearConversationCookie(domain?: string): void {
  writeCookie(CONVERSATION_COOKIE_NAME, '', { domain, days: -1 });
}
